<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { chatStore } from '@/src/store/chat';
import { usersStore } from '@/src/store/users';
import { ElDatePicker } from 'element-plus';
import { useProfileStore } from '@/src/store/profile';
import { useStartChatPopupsStore } from '@/src/store/startChatPopups';
import { useProfile } from '@/src/composables/useProfile';
import type { PreSessionQuestion } from '@/src/types/profile';
import useDateFormatter from '@/src/composables/useDateFormatter';

import Form from '@/src/helpers/form-validation';
import SePopup from '@/src/ui-kit/popup/Popup.vue';
import SeAvatar from '@/src/ui-kit/avatar/Avatar.vue';
import SeFieldMessage from '@/src/ui-kit/field-message/FieldMessage.vue';
import SeInput from '@/src/ui-kit/input/Input.vue';
import SeField from '@/src/ui-kit/field/Field.vue';
import SeTextarea from '@/src/ui-kit/textarea/Textarea.vue';
import SeSwitch from '@/src/ui-kit/switch/Switch.vue';
import SeButton from '@/src/ui-kit/button/Button.vue';
import SeRadio from '@/src/ui-kit/radio/Radio.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';

const popupStore = useStartChatPopupsStore();
const { chatApprovalActionType } = storeToRefs(popupStore);

const chats = chatStore();
const { savedAdvisor } = storeToRefs(chats);
const { startChat } = chats;

const users = usersStore();
const { currentUser } = storeToRefs(users);
const { getCurrentUser } = users;

const profile = useProfileStore();
const { preQuestionsPopupVisible } = storeToRefs(profile);
const { setPreQuestionsPopupVisible } = profile;

const { updatePreSessionQuestion } = useProfile();

const { getGlobalProperties } = useGlobalProperties();
const { $route } = getGlobalProperties();

const emptyForm: PreSessionQuestion = {
  full_name: '',
  date_of_birth: null,
  gender: '',
  question: '',
  is_about_another_person: false,
  other_person_name: '',
  other_person_date_of_birth: null,
};

const loading = ref(false);
const form = ref(new Form(emptyForm));
const actionType = computed(() =>
  chatApprovalActionType.value
    ? chatApprovalActionType.value
    : $route.query.action === 'startTextChat'
    ? 'text'
    : $route.query.action === 'startAudioChat'
    ? 'audio'
    : 'video'
);

watch(
  () => currentUser.value.pre_session_question,
  () => {
    if (currentUser.value.pre_session_question) {
      form.value = new Form(currentUser.value.pre_session_question);
    }
  }
);

watch(
  () => preQuestionsPopupVisible.value,
  (newVal) => {
    if (newVal && currentUser.value.pre_session_question) {
      form.value = new Form(currentUser.value.pre_session_question);
    }
  }
);

const isDirty = () => {
  return JSON.stringify(form.value.data()) !== JSON.stringify(new Form(emptyForm).data());
};

const saveAndStartChat = () => {
  if (isDirty()) {
    loading.value = true;
    updatePreSessionQuestion(<PreSessionQuestion>form.value.data())
      .then(() => {
        getCurrentUser();
        closePopup();
        if (savedAdvisor.value) {
          startChat(savedAdvisor.value.id, actionType.value);
        }
      })
      .finally(() => {
        loading.value = false;
      });
  } else {
    closePopup();
    if (savedAdvisor.value) {
      startChat(savedAdvisor.value.id, actionType.value);
    }
  }
};

const closePopup = () => {
  loading.value = false;
  form.value = new Form(emptyForm);
  setPreQuestionsPopupVisible(false);
};
</script>

<script lang="ts">
export default {
  name: 'SePreQuestionsPopup',
};
</script>

<template>
  <se-popup
    v-if="preQuestionsPopupVisible"
    :title="savedAdvisor ? '' : 'My Saved Question & Details'"
    @close="closePopup"
  >
    <div v-if="savedAdvisor" class="flex flex-col justify-center text-center sm:px-8">
      <div class="flex justify-center items-center mb-16">
        <se-avatar :id="savedAdvisor.id" :name="savedAdvisor.name" :src="savedAdvisor.avatar" :size="68" />
        <se-avatar
          :id="currentUser.id"
          :name="currentUser.name"
          :src="currentUser.avatar"
          :size="68"
          class="ml-[-22px]"
        />
      </div>
      <p class="text-h-xl font-bold mb-1">Get ready for your session</p>
      <p class="text-b-m">Save 3-5 min of session time by sending your details to psychic before the start.</p>
    </div>
    <form
      @submit.prevent="saveAndStartChat"
      @keydown="form.errors.clear($event.target.name)"
      class="flex flex-col gap-16 pb-32 sm:px-8 mt-16"
    >
      <se-field label-text="Full Name (optional)">
        <se-input
          id="full_name"
          v-model="form.full_name"
          name="full_name"
          size="medium"
          placeholder="Enter your name"
          type="text"
          :invalid="form.errors?.has('full_name')"
          autofocus
        />
        <se-field-message v-if="form.errors.has('full_name')" type="error">
          {{ form.errors?.get('full_name').join(', ') }}
        </se-field-message>
      </se-field>
      <se-field label-text="Date of Birth (optional)" class="relative">
        <el-date-picker v-model="form.date_of_birth" class="opacity-0 absolute !w-full !h-[48px]" />
        <se-button
          v-if="form.date_of_birth"
          type="tertiary"
          @click.stop="form.date_of_birth = null"
          class="!absolute !w-[24px] !h-[24px] right-12 bottom-[14px]"
          icon-only
          size="small"
        >
          <se-icon name="x" :size="18" />
        </se-button>
        <se-input
          placeholder="MM / DD / YYYY"
          class="pointer-events-none"
          :model-value="form.date_of_birth ? useDateFormatter().formatDate(form.date_of_birth, 'MM/dd/yyyy') : ''"
        />
        <se-field-message v-if="form.errors.has('date_of_birth')" type="error">
          {{ form.errors.get('date_of_birth').join(', ') }}
        </se-field-message>
      </se-field>
      <se-field>
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <p class="text-h-m font-semibold whitespace-nowrap">Choose your gender</p>
          <div class="gender-container flex items-center gap-12">
            <se-radio v-model="form.gender" name="gender" value="female">
              <se-button size="small" :type="form.gender === 'female' ? 'secondary' : 'tertiary'">Female</se-button>
            </se-radio>
            <se-radio v-model="form.gender" name="gender" value="male">
              <se-button size="small" :type="form.gender === 'male' ? 'secondary' : 'tertiary'">Male</se-button>
            </se-radio>
            <se-radio v-model="form.gender" name="gender" value="other">
              <se-button size="small" :type="form.gender === 'other' ? 'secondary' : 'tertiary'">Other</se-button>
            </se-radio>
          </div>
        </div>
      </se-field>
      <se-field label-text="Your Question (150 ch. left)">
        <se-textarea
          id="question"
          class="min-h-[78px]"
          v-model="form.question"
          name="question"
          placeholder="Enter your main question(s)  for the advisor..."
          :invalid="form.errors.has('question')"
        />
        <se-field-message v-if="form.errors.has('question')" type="error">
          {{ form.errors.get('question').join(', ') }}
        </se-field-message>
      </se-field>
      <se-field>
        <div class="flex items-center justify-between">
          <p class="text-h-m font-semibold">Do you have questions about other person?</p>
          <se-switch v-model="form.is_about_another_person" />
        </div>

        <se-field-message v-if="form.errors.has('is_about_another_person')" type="error">
          {{ form.errors.get('is_about_another_person').join(', ') }}
        </se-field-message>
      </se-field>
      <template v-if="form.is_about_another_person">
        <se-field label-text="Other Person's Name (optional)">
          <se-input
            id="other_person_name"
            v-model="form.other_person_name"
            name="other_person_name"
            size="medium"
            placeholder="Enter name"
            type="text"
            :invalid="form.errors.has('other_person_name')"
          />
          <se-field-message v-if="form.errors.has('other_person_name')" type="error">
            {{ form.errors.get('other_person_name').join(', ') }}
          </se-field-message>
        </se-field>

        <se-field label-text="Other Person's Date of Birth (optional)" class="relative">
          <el-date-picker v-model="form.other_person_date_of_birth" class="opacity-0 absolute !w-full !h-[48px]" />
          <se-button
            v-if="form.other_person_date_of_birth"
            type="tertiary"
            @click.stop="form.other_person_date_of_birth = null"
            class="!absolute !w-[24px] !h-[24px] right-12 bottom-[14px]"
            icon-only
            size="small"
          >
            <se-icon name="x" :size="18" />
          </se-button>
          <se-input
            placeholder="MM / DD / YYYY"
            class="pointer-events-none"
            :model-value="
              form.other_person_date_of_birth
                ? useDateFormatter().formatDate(form.other_person_date_of_birth, 'MM/dd/yyyy')
                : ''
            "
          />
          <se-field-message v-if="form.errors.has('other_person_date_of_birth')" type="error">
            {{ form.errors.get('other_person_date_of_birth').join(', ') }}
          </se-field-message>
        </se-field>
      </template>
      <se-button type="primary" size="large" native-type="submit" :loading="loading" class="w-full">
        {{ savedAdvisor ? 'Save & Start Chat' : 'Save' }}
      </se-button>
      <p v-if="savedAdvisor" class="text-b-s text-neutral-60">
        Your answers will be saved in your Profile, where you will be able to edit or delete them.
      </p>
    </form>
  </se-popup>
</template>

<style lang="scss">
.gender-container {
  .c-radio {
    input {
      @apply opacity-0 absolute w-full h-full z-10;
    }
  }
}

.el-popper.el-picker__popper {
  z-index: 100000 !important;
}
</style>
